import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Search, Printer, RefreshCw, X, Filter, ChevronLeft, ChevronRight, FileText, Layers, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const PAGE_SIZE = 50;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const statusClass = (s) => {
  const v = (s || '').toLowerCase();
  if (v.includes('reject')) return 'bg-red-100 text-red-700 border-red-200';
  if (v.includes('complete') || v === 'received') return 'bg-green-100 text-green-700 border-green-200';
  if (v === 'shipped') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (v === 'accepted') return 'bg-teal-100 text-teal-700 border-teal-200';
  return 'bg-amber-100 text-amber-700 border-amber-200';
};

// --- Per-transfer print builders (identical to the Internal Transfer screen) ---
const esc = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

// Auto-print trigger embedded in the printable HTML itself — fires on the new
// document's load, which is reliable even for very large tables (a fixed
// setTimeout used to race the render and mis/never-fire on big sheets).
const PRINT_TRIGGER = `<script>window.onload=function(){try{window.focus();window.print();}catch(e){}};</script>`;

// Open the print window SYNCHRONOUSLY inside the click handler (before any
// await). Popup blockers kill window.open() when it runs after an await, and
// the per-transfer detail fetch can take several seconds for a large transfer
// (~6 DB lookups per bundle) — which is exactly why printing "failed" once a
// transfer had many lines. We open first, show a placeholder, then fill it.
const openBlankPrintWindow = () => {
  const win = window.open('', '_blank');
  if (win) {
    win.document.write('<!doctype html><html><head><meta charset="utf-8"><title>Preparing print…</title></head><body style="font-family:Arial;padding:28px;color:#555;font-size:14px">Preparing print… please wait.</body></html>');
    win.document.close();
  }
  return win;
};

const fillPrintWindow = (win, html) => {
  if (!win || win.closed) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
};

const failPrintWindow = (win, msg) => {
  if (!win || win.closed) return;
  win.document.open();
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"></head><body style="font-family:Arial;padding:28px;color:#b91c1c;font-size:14px">${esc(msg)}</body></html>`);
  win.document.close();
};

const PRINT_STYLE = `
  * { font-family: Arial, Helvetica, sans-serif; }
  body { margin: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .sub { font-size: 12px; color: #555; margin-bottom: 14px; }
  table.meta { width: 100%; font-size: 12px; margin-bottom: 14px; border-collapse: collapse; }
  table.meta td { padding: 2px 6px; }
  table.meta .k { color: #666; width: 90px; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 12px; }
  table.grid th, table.grid td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
  table.grid th { background: #f0f0f0; }
  td.c, th.c { text-align: center; }
  .b { font-weight: bold; }
  tfoot td { font-weight: bold; background: #fafafa; }
  @media print { body { margin: 10mm; } }
`;

const buildTransferSheetHtml = (t) => {
  if (!t) return '';
  // Group by Sort No + Shade so the added Shade column is well-defined per row
  // (a sort can carry more than one shade) while qty totals stay correct.
  const groups = {};
  (t.bundles || []).forEach((b) => {
    const sortNo = b.sort_no || '—';
    const shade = b.shade || '';  // rates_master.shade for the sort's SKU (NOT Kuri Color)
    const key = `${sortNo}||${shade}`;
    if (!groups[key]) {
      groups[key] = {
        sort_no: sortNo, shade, border: b.border || '', size: b.size || '',
        warp_count: b.warp_count || '', weft_count: b.weft_count || '',
        reed: b.reed || '', pick: b.pick || '', width: b.width || '', qty: 0,
      };
    }
    groups[key].qty += Number(b.qty || 0);
    ['border', 'size', 'warp_count', 'weft_count', 'reed', 'pick', 'width'].forEach((f) => {
      if (!groups[key][f] && b[f]) groups[key][f] = b[f];
    });
  });
  const rows = Object.values(groups).sort((a, b) => {
    const s = String(a.sort_no).localeCompare(String(b.sort_no), undefined, { numeric: true });
    return s !== 0 ? s : String(a.shade).localeCompare(String(b.shade));
  });
  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const distinctSorts = new Set(rows.map((r) => r.sort_no)).size;
  const bodyRows = rows.map((r) => `<tr>
      <td>${esc(r.sort_no)}</td><td>${esc(r.shade)}</td><td>${esc(r.border)}</td><td>${esc(r.size)}</td>
      <td class="c">${esc(r.warp_count)}</td><td class="c">${esc(r.weft_count)}</td>
      <td class="c">${esc(r.reed)}</td><td class="c">${esc(r.pick)}</td><td class="c">${esc(r.width)}</td>
      <td class="c b">${esc(r.qty)}</td></tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Internal Transfer ${esc(t.transfer_id)}</title>
    <style>${PRINT_STYLE}</style></head><body>
    <h1>Internal Transfer Sheet</h1>
    <div class="sub">Minister White — Greige Storage</div>
    <table class="meta">
      <tr><td class="k">Transfer ID</td><td><b>${esc(t.transfer_id || '-')}</b></td>
          <td class="k">Date</td><td>${esc(t.date)}</td></tr>
      <tr><td class="k">From</td><td>${esc(t.from_section)}</td>
          <td class="k">To</td><td>${esc(t.to_section)}</td></tr>
      <tr><td class="k">Staff</td><td>${esc(t.staff)}</td>
          <td class="k">Sort Nos</td><td>${distinctSorts}</td></tr>
      <tr><td class="k">ERP Entry</td><td>${esc(t.erp_entry_no || '—')}</td>
          <td class="k">Remarks</td><td>${esc(t.remarks || '—')}</td></tr>
    </table>
    <table class="grid">
      <thead><tr>
        <th>Sort No</th><th>Shade</th><th>Border</th><th>Size</th><th class="c">Warp Count</th>
        <th class="c">Weft Count</th><th class="c">Reed</th><th class="c">Pick</th>
        <th class="c">Width</th><th class="c">Qty</th>
      </tr></thead>
      <tbody>${bodyRows}</tbody>
      <tfoot><tr><td colspan="9" style="text-align:right">Total Qty</td><td class="c">${totalQty}</td></tr></tfoot>
    </table>
    ${PRINT_TRIGGER}
    </body></html>`;
};

const buildBundleWiseHtml = (t) => {
  if (!t) return '';
  const rows = [...(t.bundles || [])].sort((a, b) =>
    String(a.barcode).localeCompare(String(b.barcode), undefined, { numeric: true }));
  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const totalWeight = rows.reduce((s, r) => s + (Number(r.bundle_weight) || 0), 0);
  const anyWeight = rows.some((r) => r.bundle_weight !== '' && r.bundle_weight != null);
  const bodyRows = rows.map((r, i) => `<tr>
      <td class="c">${i + 1}</td>
      <td>${esc(r.barcode)}</td>
      <td>${esc(r.color_name)}</td>
      <td class="c b">${esc(r.qty ?? 0)}</td>
      <td class="c">${esc(r.inspection_width !== '' && r.inspection_width != null ? r.inspection_width : '')}</td>
      <td class="c">${esc(r.bundle_weight !== '' && r.bundle_weight != null ? r.bundle_weight : '')}</td>
    </tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Bundle-Wise ${esc(t.transfer_id)}</title>
    <style>${PRINT_STYLE}</style></head><body>
    <h1>Bundle-Wise Transfer Sheet</h1>
    <div class="sub">Minister White — Greige Storage</div>
    <table class="meta">
      <tr><td class="k">Transfer ID</td><td><b>${esc(t.transfer_id || '-')}</b></td>
          <td class="k">Date</td><td>${esc(t.date)}</td></tr>
      <tr><td class="k">From</td><td>${esc(t.from_section)}</td>
          <td class="k">To</td><td>${esc(t.to_section)}</td></tr>
      <tr><td class="k">Staff</td><td>${esc(t.staff)}</td>
          <td class="k">Bundles</td><td>${rows.length}</td></tr>
      <tr><td class="k">ERP Entry</td><td>${esc(t.erp_entry_no || '—')}</td>
          <td class="k">Remarks</td><td>${esc(t.remarks || '—')}</td></tr>
    </table>
    <table class="grid">
      <thead><tr>
        <th class="c">#</th><th>Bundle No</th><th>Shade</th>
        <th class="c">Qty</th><th class="c">Width</th><th class="c">Weight (kg)</th>
      </tr></thead>
      <tbody>${bodyRows}</tbody>
      <tfoot><tr><td colspan="3" style="text-align:right">Total</td><td class="c">${totalQty}</td><td></td><td class="c">${anyWeight ? totalWeight.toFixed(2) : ''}</td></tr></tfoot>
    </table>
    ${PRINT_TRIGGER}
    </body></html>`;
};


const InternalTransferReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_transfers: 0, total_bundles: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rowPrinting, setRowPrinting] = useState('');   // `${transfer_id}:${kind}` in flight
  const [erpValues, setErpValues] = useState({});       // transfer_id -> current input value
  const [erpSaving, setErpSaving] = useState('');       // transfer_id being saved

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [fgDesign, setFgDesign] = useState('');
  const [shade, setShade] = useState('');

  const paramsFrom = (f, extra = {}) => {
    const p = { page: 1, page_size: PAGE_SIZE, ...extra };
    if (f.dateFrom) p.date_from = f.dateFrom;
    if (f.dateTo) p.date_to = f.dateTo;
    if ((f.search || '').trim()) p.search = f.search.trim();
    if ((f.fgDesign || '').trim()) p.fg_design = f.fgDesign.trim();
    if ((f.shade || '').trim()) p.shade = f.shade.trim();
    return p;
  };

  const fetchReport = useCallback(async (targetPage = 1, overrides = null) => {
    const f = overrides || { dateFrom, dateTo, search, fgDesign, shade };
    setLoading(true);
    try {
      const data = await greigeStorageApi.getInternalTransferReport(paramsFrom(f, { page: targetPage }));
      setRows(data.rows || []);
      const emap = {};
      (data.rows || []).forEach((r) => { emap[r.transfer_id] = r.erp_entry_no || ''; });
      setErpValues(emap);
      setSummary(data.summary || { total_transfers: 0, total_bundles: 0 });
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || targetPage);
    } catch (err) {
      console.error('Failed to load internal transfer report:', err);
      toast.error('Failed to load internal transfer report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, search, fgDesign, shade]);

  useEffect(() => {
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => fetchReport(1);
  const handleClear = () => {
    setDateFrom(''); setDateTo(''); setSearch(''); setFgDesign(''); setShade('');
    fetchReport(1, { dateFrom: '', dateTo: '', search: '', fgDesign: '', shade: '' });
  };

  // Persist the per-row ERP entry number (save on blur / Enter, only if changed).
  const saveErp = async (r) => {
    const val = (erpValues[r.transfer_id] ?? '').trim();
    if (val === (r.erp_entry_no || '')) return;
    setErpSaving(r.transfer_id);
    try {
      await greigeStorageApi.saveInternalTransferErpEntry(r.transfer_id, val);
      setRows((prev) => prev.map((x) => (x.transfer_id === r.transfer_id ? { ...x, erp_entry_no: val } : x)));
      toast.success(`ERP entry saved for ${r.transfer_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save ERP entry');
    } finally {
      setErpSaving('');
    }
  };

  // Fetch the transfer's enriched bundle detail, then print the requested sheet.
  const handleRowPrint = async (r, kind) => {
    const key = `${r.transfer_id}:${kind}`;
    // Open the window NOW (inside the click gesture) so a slow detail fetch on a
    // large transfer can't cause the popup to be blocked and the print to fail.
    const win = openBlankPrintWindow();
    if (!win) { toast.error('Popup blocked — allow popups to print the sheet'); return; }
    setRowPrinting(key);
    try {
      const detail = await greigeStorageApi.getInternalTransferDetail(r.transfer_id);
      if (!detail || !(detail.bundles || []).length) {
        toast.error('No bundle detail found for this transfer');
        failPrintWindow(win, 'No bundle detail found for this transfer.');
        return;
      }
      const t = { ...detail, date: fmtDate(detail.date) };
      const html = kind === 'bundlewise' ? buildBundleWiseHtml(t) : buildTransferSheetHtml(t);
      fillPrintWindow(win, html);
    } catch (err) {
      console.error('Row print failed:', err);
      toast.error('Failed to load transfer detail for printing');
      failPrintWindow(win, 'Failed to load transfer detail for printing.');
    } finally {
      setRowPrinting('');
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const blob = await greigeStorageApi.exportInternalTransferReportCsv(
        paramsFrom({ dateFrom, dateTo, search, fgDesign, shade }, { page: 1 }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `greige_storage_internal_transfers_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = async () => {
    // Open synchronously first (see handleRowPrint) — the full-set fetch of up to
    // 500 transfers can take a while and would otherwise be popup-blocked.
    const win = openBlankPrintWindow();
    if (!win) { toast.error('Popup blocked — allow popups to print the report'); return; }
    setPrinting(true);
    try {
      // Pull the full filtered set (max 500) so the printout is complete
      // regardless of the on-screen page.
      const data = await greigeStorageApi.getInternalTransferReport(
        paramsFrom({ dateFrom, dateTo, search, fgDesign, shade }, { page: 1, page_size: 500 }));
      const printRows = data.rows || [];
      if (printRows.length === 0) {
        toast.error('No transfers to print');
        failPrintWindow(win, 'No transfers to print.');
        return;
      }
      const totBundles = printRows.reduce((s, r) => s + Number(r.bundle_count || 0), 0);
      const totQty = printRows.reduce((s, r) => s + Number(r.qty || 0), 0);
      const rangeTxt = (dateFrom || dateTo)
        ? `${dateFrom || '…'} to ${dateTo || '…'}` : 'All dates';
      const body = printRows.map((r, i) => `<tr>
          <td class="c">${i + 1}</td>
          <td>${esc(r.transfer_id)}</td>
          <td>${esc(fmtDate(r.date))}</td>
          <td>${esc(r.to_section)}</td>
          <td class="c">${esc(r.transfer_type)}</td>
          <td class="c b">${esc(r.bundle_count)}</td>
          <td class="c b">${esc(r.qty)}</td>
          <td>${esc(r.staff_name)}${r.staff_id ? ` (${esc(r.staff_id)})` : ''}</td>
          <td>${esc(r.status)}</td>
          <td>${esc(r.remarks)}</td>
          <td>${esc(r.erp_entry_no)}</td>
        </tr>`).join('');
      const html = `<!doctype html><html><head><meta charset="utf-8">
        <title>Greige Storage — Internal Transfer Report</title>
        <style>
          * { font-family: Arial, Helvetica, sans-serif; }
          body { margin: 22px; color: #111; }
          h1 { font-size: 18px; margin: 0 0 2px; }
          .sub { font-size: 12px; color: #555; margin-bottom: 12px; }
          table.grid { width: 100%; border-collapse: collapse; font-size: 11px; }
          table.grid th, table.grid td { border: 1px solid #333; padding: 5px 7px; text-align: left; vertical-align: top; }
          table.grid th { background: #f0f0f0; }
          td.c, th.c { text-align: center; }
          .b { font-weight: bold; }
          tfoot td { font-weight: bold; background: #fafafa; }
          @media print { body { margin: 10mm; } }
        </style></head><body>
        <h1>Internal Transfer Report — Greige Storage</h1>
        <div class="sub">Minister White &nbsp;·&nbsp; Date range: ${esc(rangeTxt)}${data.total > printRows.length ? ` &nbsp;·&nbsp; showing first ${printRows.length} of ${data.total}` : ''} &nbsp;·&nbsp; Printed: ${esc(new Date().toLocaleString())}</div>
        <table class="grid">
          <thead><tr>
            <th class="c">#</th><th>Transfer ID</th><th>Date</th><th>To Section</th>
            <th class="c">Type</th><th class="c">Bundles</th><th class="c">Qty</th>
            <th>Staff</th><th>Status</th><th>Remarks</th><th>ERP Entry No</th>
          </tr></thead>
          <tbody>${body}</tbody>
          <tfoot><tr>
            <td colspan="5" style="text-align:right">Totals (${printRows.length} transfers)</td>
            <td class="c">${totBundles}</td><td class="c">${totQty}</td>
            <td colspan="4"></td>
          </tr></tfoot>
        </table>
        ${PRINT_TRIGGER}
      </body></html>`;
      fillPrintWindow(win, html);
    } catch (err) {
      console.error('Print failed:', err);
      toast.error('Failed to prepare the printable report');
      failPrintWindow(win, 'Failed to prepare the printable report.');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="greige-it-report">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/sections/greige-storage')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold font-display">Internal Transfer Report</h1>
                <p className="text-sm text-[#E8DCC8]">Transfers out of Greige Storage</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={exporting || loading}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="export-csv-btn"
            >
              {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              disabled={printing || loading}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="print-report-btn"
            >
              {printing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Print Report
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Transfers</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="stat-transfers">{(summary.total_transfers || 0).toLocaleString()}</p>
                </div>
                <ArrowRightLeft className="w-8 h-8 text-[#2D5F4F]" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Bundles</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="stat-bundles">{(summary.total_bundles || 0).toLocaleString()}</p>
                </div>
                <ArrowRightLeft className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Date From</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date To</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="filter-date-to"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Search (anything)</label>
                <Input
                  placeholder="Transfer ID, destination, staff, status, bundle…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="filter-search"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">FG Design Name</label>
                <Input
                  placeholder="e.g. Mona"
                  value={fgDesign}
                  onChange={(e) => setFgDesign(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="filter-fg-design"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Shade</label>
                <Input
                  placeholder="e.g. Blue"
                  value={shade}
                  onChange={(e) => setShade(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="filter-shade"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={loading}
                  data-testid="search-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
                <Button variant="outline" onClick={handleClear} data-testid="clear-filters-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Transfer ID</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">To Section</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">FG Design Name</TableHead>
                    <TableHead className="font-semibold">Shade</TableHead>
                    <TableHead className="font-semibold text-right">Bundles</TableHead>
                    <TableHead className="font-semibold text-right">Qty</TableHead>
                    <TableHead className="font-semibold">Staff</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Remarks</TableHead>
                    <TableHead className="font-semibold">ERP Entry No</TableHead>
                    <TableHead className="font-semibold text-center">Print</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-gray-500" data-testid="empty-row">
                        No internal transfers found. Try adjusting the filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.transfer_id} className="hover:bg-gray-50" data-testid={`it-row-${r.transfer_id}`}>
                        <TableCell className="font-mono text-sm font-semibold" data-testid={`it-id-${r.transfer_id}`}>{r.transfer_id}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap" data-testid={`it-date-${r.transfer_id}`}>{fmtDate(r.date)}</TableCell>
                        <TableCell data-testid={`it-to-${r.transfer_id}`}>{r.to_section || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={r.transfer_type === 'REWORK' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-gray-100 text-gray-600 border-gray-200'}>
                            {r.transfer_type || 'STANDARD'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px]" title={r.fg_item || ''} data-testid={`it-fg-design-${r.transfer_id}`}>{r.fg_design_name || '—'}</TableCell>
                        <TableCell className="text-sm max-w-[160px]" data-testid={`it-shade-${r.transfer_id}`}>{r.shade || '—'}</TableCell>
                        <TableCell className="text-right font-semibold" data-testid={`it-bundles-${r.transfer_id}`}>{r.bundle_count}</TableCell>
                        <TableCell className="text-right font-semibold text-[#2D5F4F]" data-testid={`it-qty-${r.transfer_id}`}>{r.qty}</TableCell>
                        <TableCell className="text-sm">{r.staff_name || '—'}{r.staff_id ? <span className="text-gray-400"> ({r.staff_id})</span> : null}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusClass(r.status)}>{r.status || '—'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate" title={r.remarks || ''}>{r.remarks || '—'}</TableCell>
                        <TableCell>
                          <Input
                            value={erpValues[r.transfer_id] ?? ''}
                            onChange={(e) => setErpValues((prev) => ({ ...prev, [r.transfer_id]: e.target.value }))}
                            onBlur={() => saveErp(r)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            placeholder="ERP no…"
                            className="h-8 w-32 text-sm"
                            disabled={erpSaving === r.transfer_id}
                            data-testid={`erp-input-${r.transfer_id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="outline" size="sm"
                              className="h-8 px-2"
                              onClick={() => handleRowPrint(r, 'transfer')}
                              disabled={rowPrinting !== ''}
                              title="Print Transfer Sheet (sort-no wise)"
                              data-testid={`print-transfer-${r.transfer_id}`}
                            >
                              {rowPrinting === `${r.transfer_id}:transfer`
                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : <FileText className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              className="h-8 px-2"
                              onClick={() => handleRowPrint(r, 'bundlewise')}
                              disabled={rowPrinting !== ''}
                              title="Print Bundle-Wise sheet"
                              data-testid={`print-bundlewise-${r.transfer_id}`}
                            >
                              {rowPrinting === `${r.transfer_id}:bundlewise`
                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                : <Layers className="w-4 h-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between items-center gap-3">
              <span className="text-sm text-gray-600" data-testid="page-info">
                {total > 0 ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()} transfers` : 'No transfers'}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => fetchReport(page - 1)}
                  disabled={loading || page <= 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => fetchReport(page + 1)}
                  disabled={loading || page >= totalPages}
                  data-testid="next-page-btn"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InternalTransferReport;
